/**
 * Emma Session Sync Hook
 * ADDITIVE LAYER - Background sync that doesn't affect existing behavior
 * 
 * BMad Team:
 * - Jorge (Integration): Hook wiring
 * - Quinn (Chaos): Failure resilience
 * - Priya (Performance): Debouncing
 * 
 * BREAK NOTHING MANDATE: 
 * - Runs silently in background
 * - Never throws, only logs warnings
 * - localStorage remains source of truth
 */

import { useEffect, useRef, useCallback } from 'react';
import emmaSessionApi, { EmmaSessionData } from '../../services/emma/emmaSessionApi';

interface UseEmmaSessionSyncOptions {
  /** Session ID from EmmaContext */
  sessionId: string | null;
  /** Current messages to sync */
  messages: Array<{ id: string; content: string; sender: 'user' | 'emma'; timestamp: Date }>;
  /** Current context state */
  contextState: Record<string, any>;
  /** Callback when backend session is restored */
  onSessionRestored?: (data: EmmaSessionData) => void;
  /** Sync interval in ms (default: 30000 = 30s) */
  syncInterval?: number;
  /** Enable sync (default: true) */
  enabled?: boolean;
}

interface SyncState {
  backendSessionKey: string | null;
  lastSyncedMessageCount: number;
  lastSyncedContextHash: string;
  syncInProgress: boolean;
}

const hashObject = (obj: unknown): string => {
  try {
    return btoa(JSON.stringify(obj)).slice(0, 32);
  } catch {
    return 'hash-error';
  }
};

const getDeviceId = (): string => {
  let deviceId = localStorage.getItem('emma_device_id');
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('emma_device_id', deviceId);
  }
  return deviceId;
};

const getDeviceType = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad/.test(ua)) return 'mobile';
  if (/tablet|ipad/.test(ua)) return 'tablet';
  return 'desktop';
};

export function useEmmaSessionSync({
  sessionId,
  messages,
  contextState,
  onSessionRestored,
  syncInterval = 30000,
  enabled = true,
}: UseEmmaSessionSyncOptions) {
  const syncStateRef = useRef<SyncState>({
    backendSessionKey: localStorage.getItem('emma_backend_session_key'),
    lastSyncedMessageCount: 0,
    lastSyncedContextHash: '',
    syncInProgress: false,
  });

  // Initialize or restore backend session
  const initBackendSession = useCallback(async () => {
    if (!enabled || !emmaSessionApi.isBackendSessionEnabled()) return;
    if (syncStateRef.current.syncInProgress) return;

    const state = syncStateRef.current;

    // If we have a stored backend session key, try to restore it
    if (state.backendSessionKey) {
      const result = await emmaSessionApi.getSession(state.backendSessionKey);
      if (result.success && result.data) {
        console.log('[EmmaSync] Restored backend session:', state.backendSessionKey);
        onSessionRestored?.(result.data);
        return;
      }
      // Session expired or invalid, clear it
      localStorage.removeItem('emma_backend_session_key');
      state.backendSessionKey = null;
    }

    // Create new backend session
    const deviceId = getDeviceId();
    const deviceType = getDeviceType();
    const result = await emmaSessionApi.createSession(deviceId, deviceType);
    
    if (result.success && result.data) {
      state.backendSessionKey = result.data.session_key;
      localStorage.setItem('emma_backend_session_key', result.data.session_key);
      console.log('[EmmaSync] Created backend session:', result.data.session_key);
    }
  }, [enabled, onSessionRestored]);

  // Sync messages to backend (debounced)
  const syncMessages = useCallback(async () => {
    if (!enabled || !emmaSessionApi.isBackendSessionEnabled()) return;
    
    const state = syncStateRef.current;
    if (!state.backendSessionKey || state.syncInProgress) return;

    // Only sync new messages
    const newMessages = messages.slice(state.lastSyncedMessageCount);
    if (newMessages.length === 0) return;

    state.syncInProgress = true;

    try {
      for (const msg of newMessages) {
        await emmaSessionApi.saveMessage(
          state.backendSessionKey,
          msg.sender === 'user' ? 'user' : 'emma',
          msg.content,
          { originalId: msg.id, timestamp: msg.timestamp.toISOString() }
        );
      }
      state.lastSyncedMessageCount = messages.length;
      console.log(`[EmmaSync] Synced ${newMessages.length} messages`);
    } catch (err) {
      console.warn('[EmmaSync] Message sync failed:', err);
    } finally {
      state.syncInProgress = false;
    }
  }, [enabled, messages]);

  // Sync context to backend
  const syncContext = useCallback(async () => {
    if (!enabled || !emmaSessionApi.isBackendSessionEnabled()) return;
    
    const state = syncStateRef.current;
    if (!state.backendSessionKey || state.syncInProgress) return;

    const currentHash = hashObject(contextState);
    if (currentHash === state.lastSyncedContextHash) return;

    state.syncInProgress = true;

    try {
      await emmaSessionApi.updateContext(state.backendSessionKey, contextState);
      state.lastSyncedContextHash = currentHash;
      console.log('[EmmaSync] Context synced');
    } catch (err) {
      console.warn('[EmmaSync] Context sync failed:', err);
    } finally {
      state.syncInProgress = false;
    }
  }, [enabled, contextState]);

  // Initialize on mount
  useEffect(() => {
    initBackendSession();
  }, [initBackendSession]);

  // Periodic sync
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      syncMessages();
      syncContext();
    }, syncInterval);

    return () => clearInterval(interval);
  }, [enabled, syncInterval, syncMessages, syncContext]);

  // Sync on unmount/page leave
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Fire-and-forget sync attempt
      syncMessages();
      syncContext();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [syncMessages, syncContext]);

  return {
    backendSessionKey: syncStateRef.current.backendSessionKey,
    isBackendEnabled: emmaSessionApi.isBackendSessionEnabled(),
    forceSync: useCallback(() => {
      syncMessages();
      syncContext();
    }, [syncMessages, syncContext]),
  };
}

export default useEmmaSessionSync;
